import "../client/styles.css";
import {
  DEFAULT_RETENTION,
  FILE_CHUNK_BYTES,
  MAX_FILE_BYTES,
  MAX_FILE_LABEL,
  type Retention
} from "../shared/constants";
import {
  type EncryptedPayload,

  decryptBytes,
  decryptText,
  deriveKeyFromCode,
  encryptBytes,
  encryptText,
  toArrayBuffer,
  importPasteKey
} from "../shared/crypto";
import {
  newestTextBubbles,
  parseTextBubbles,
  serializeTextBubbles,
  withoutTextBubble,
  type TextBubble
} from "../shared/text-bubbles";

type PasteResponse = {
  id: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  text: EncryptedPayload | null;
  files: StoredFile[];
};

type StoredFile = {
  id: string;
  encryptedName: EncryptedPayload;
  encryptedType: EncryptedPayload;
  plainSize: number;
  createdAt: string;
  parts: StoredPart[];
};

type StoredPart = {
  partNumber: number;
  iv: string;
  size: number;
};

type DisplayFile = StoredFile & {
  name: string;
  type: string;
};

const root = document.querySelector<HTMLDivElement>("#app");
if (!root) {
  throw new Error("Missing app root");
}
const app: HTMLDivElement = root;

let pasteId = getPasteId();
let pasteCode: string | null = null;
let pasteKey: CryptoKey | null = null;
let keyText = getKeyText();
let bubbles: TextBubble[] = [];
let draftText = "";
let expiresAt: string | null = null;
let retention: Retention = DEFAULT_RETENTION;
let files: DisplayFile[] = [];
const expandedBubbles = new Set<string>();
let statusText = "";
let statusType: "info" | "success" | "error" = "info";
let busy = false;

void boot();

async function boot(): Promise<void> {
  const code = getShortCode();
  if (code) {
    pasteCode = code;
    pasteId = code;
    if (keyText) {
      pasteKey = await importPasteKey(keyText);
    } else {
      pasteKey = await deriveKeyFromCode(code);
    }
    await loadPaste();
  } else if (pasteId && keyText) {
    pasteKey = await importPasteKey(keyText);
    await loadPaste();
  } else {
    render();
  }
}

// --- Rendering ---

function render({ preserveTextarea = true }: { preserveTextarea?: boolean } = {}): void {
  const existingTextarea = document.querySelector<HTMLTextAreaElement>("#pasteText");
  const hadFocus = existingTextarea === document.activeElement;
  const cursorStart = existingTextarea?.selectionStart ?? 0;
  const cursorEnd = existingTextarea?.selectionEnd ?? 0;
  const liveText = existingTextarea?.value;

  app.innerHTML = `
    <section class="shell">
      <header class="topbar">
        <div class="brand">
          <div class="logo-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          </div>
          <div>
            <p class="eyebrow">End-to-End Encrypted</p>
            <h1>Sharebin</h1>
          </div>
        </div>
        <div class="actions">
          <label class="retention-picker">
            <span>New paste retention</span>
            <select id="retention" ${busy ? "disabled" : ""}>
              <option value="5min" ${retention === "5min" ? "selected" : ""}>5 min</option>
              <option value="1hr" ${retention === "1hr" ? "selected" : ""}>1 hr</option>
              <option value="4hr" ${retention === "4hr" ? "selected" : ""}>4 hr</option>
              <option value="24hr" ${retention === "24hr" ? "selected" : ""}>24 hr</option>
              <option value="forever" ${retention === "forever" ? "selected" : ""}>Forever</option>
            </select>
          </label>
          <button id="newPaste" type="button" class="btn-primary" ${busy ? "disabled" : ""}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New
          </button>
          ${pasteId ? `<button id="refreshPaste" type="button" class="btn-ghost" ${busy ? "disabled" : ""}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Sync
          </button>` : ""}
        </div>
      </header>

      ${pasteTemplate()}
      ${statusText ? `<div class="toast ${statusType}" role="status"><span>${escapeHtml(statusText)}</span></div>` : ""}
    </section>
  `;

  const newTextarea = document.querySelector<HTMLTextAreaElement>("#pasteText");
  if (preserveTextarea && newTextarea && liveText !== undefined) {
    newTextarea.value = liveText;
    if (hadFocus) {
      newTextarea.focus();
      newTextarea.setSelectionRange(cursorStart, cursorEnd);
    }
  }

  bindEvents();
  clearStatusAfterDelay();
}

let statusTimer: ReturnType<typeof setTimeout> | null = null;
function clearStatusAfterDelay(): void {
  if (statusTimer) clearTimeout(statusTimer);
  if (statusText && statusType !== "error") {
    statusTimer = setTimeout(() => {
      statusText = "";
      const toast = document.querySelector<HTMLDivElement>(".toast");
      if (toast) {
        toast.classList.add("fade-out");
        setTimeout(() => { toast.remove(); }, 300);
      }
    }, 3500);
  }
}

function setStatus(text: string, type: "info" | "success" | "error" = "info"): void {
  statusText = text;
  statusType = type;
}

function pasteTemplate(): string {
  const shareCode = pasteCode !== null ? pasteCode : null;
  const shareLink = getShareLink();

  const expiryStr = pasteId && expiresAt === null ? "Forever" : expiresAt ? formatRelativeTime(expiresAt) : `${retention} retention`;

  return `
    <section class="workspace">
      <div class="editor glass">
        <div class="meta">
          <span class="paste-label">${shareCode
            ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"/></svg> ${escapeHtml(shareCode)}`
            : pasteId
              ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> ${escapeHtml(pasteId.slice(0, 12))}…`
              : "No paste open"}</span>
          <span class="expiry-label">${expiryStr}</span>
        </div>
        <div class="bubble-list">
          ${bubbles.length ? newestTextBubbles(bubbles).map(textBubbleTemplate).join("") : `<p class="empty bubble-empty">No text added yet.</p>`}
        </div>
        <div class="composer">
          <textarea id="pasteText" spellcheck="false" placeholder="Add text to this sharebin…">${escapeHtml(draftText)}</textarea>
          <button id="saveText" type="button" class="btn-primary" ${busy ? "disabled" : ""}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add & save
          </button>
        </div>
        <div class="toolbar">
          <div class="toolbar-left">
            <button id="copyText" type="button" class="btn-ghost" ${!bubbles.length ? "disabled" : ""}>Copy all</button>
            <button id="copyLink" type="button" class="btn-ghost" ${!shareLink ? "disabled" : ""}>Copy link</button>
          </div>
          <button id="deletePaste" type="button" class="btn-danger" ${!pasteId || busy ? "disabled" : ""}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            Delete
          </button>
        </div>
        ${shareCode && shareLink ? `<div class="share-bar"><code class="share-code">${escapeHtml(shareCode)}</code><input class="share-input" readonly value="${escapeHtml(shareLink)}" /></div>` : ""}
        ${!shareCode && shareLink ? `<div class="share-bar"><input class="share-input" readonly value="${escapeHtml(shareLink)}" /></div>` : ""}
        ${!pasteId && !shareCode ? `<div class="open-section">
          <label for="openCode">Open existing paste</label>
          <div class="row">
            <input id="openCode" type="text" placeholder="e.g. a3f8-b2k1" />
            <button id="openPaste" type="button" class="btn-ghost">Open</button>
          </div>
        </div>` : ""}
      </div>

      <aside class="files glass">
        <div class="file-head">
          <h2>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>
            Files
          </h2>
          <span class="file-limit">${MAX_FILE_LABEL}</span>
        </div>

        <label id="dropZone" class="drop-zone ${!pasteId || busy ? "disabled" : ""}">
          <input id="fileInput" type="file" ${!pasteId || busy ? "disabled" : ""} />
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          <span>Choose file or drag here</span>
        </label>

        <div class="file-list">
          ${
            files.length
              ? files
                  .map(
                    (file) => `
                      <article class="file-item">
                        <div class="file-info">
                          <strong>${escapeHtml(file.name || "download")}</strong>
                          <span>${formatBytes(file.plainSize)}</span>
                        </div>
                        <div class="file-actions">
                          <button data-file-id="${escapeHtml(file.id)}" class="btn-ghost download" type="button" aria-label="Download file" ${busy ? "disabled" : ""}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          </button>
                          <button data-file-delete="${escapeHtml(file.id)}" class="btn-ghost file-delete" type="button" aria-label="Delete file" ${busy ? "disabled" : ""}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </article>
                    `
                  )
                  .join("")
              : `<p class="empty">No files uploaded yet.</p>`
          }
        </div>
      </aside>
    </section>
  `;
}

function textBubbleTemplate(bubble: TextBubble): string {
  const isLong = bubble.text.length > 500 || bubble.text.split("\n").length > 8;
  const expanded = expandedBubbles.has(bubble.id);
  return `
    <article class="text-bubble ${isLong && !expanded ? "collapsed" : ""}">
      <div class="bubble-actions">
        ${isLong ? `<button data-bubble-expand="${escapeHtml(bubble.id)}" class="btn-ghost bubble-action" type="button" ${busy ? "disabled" : ""}>${expanded ? "Collapse" : "Expand"}</button>` : ""}
        <button data-bubble-copy="${escapeHtml(bubble.id)}" class="btn-ghost bubble-action" type="button" aria-label="Copy text" ${busy ? "disabled" : ""}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        </button>
        <button data-bubble-delete="${escapeHtml(bubble.id)}" class="btn-ghost bubble-action bubble-delete" type="button" aria-label="Delete text" ${busy ? "disabled" : ""}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
        </button>
      </div>
      <pre>${escapeHtml(bubble.text)}</pre>
    </article>
  `;
}

function bindEvents(): void {
  document.querySelector<HTMLSelectElement>("#retention")?.addEventListener("change", (event) => {
    retention = (event.currentTarget as HTMLSelectElement).value as Retention;
  });
  document.querySelector("#newPaste")?.addEventListener("click", () => void createPaste());
  document.querySelector("#refreshPaste")?.addEventListener("click", () => void loadPaste());
  document.querySelector("#saveText")?.addEventListener("click", () => void saveText());
  document.querySelector("#copyLink")?.addEventListener("click", () => void copyLink());
  document.querySelector("#copyText")?.addEventListener("click", () => void copyText());
  document.querySelector("#deletePaste")?.addEventListener("click", () => void deletePaste());
  document.querySelector("#openPaste")?.addEventListener("click", () => void openPaste());
  document.querySelector("#openCode")?.addEventListener("keydown", (event) => {
    if ((event as KeyboardEvent).key === "Enter") void openPaste();
  });
  document.querySelector("#pasteText")?.addEventListener("keydown", (event) => {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === "Enter" && (keyboardEvent.metaKey || keyboardEvent.ctrlKey)) {
      keyboardEvent.preventDefault();
      void saveText();
    }
  });
  document.querySelectorAll<HTMLButtonElement>("[data-bubble-copy]").forEach((button) => {
    button.addEventListener("click", () => void copyBubble(button.dataset.bubbleCopy ?? ""));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-bubble-expand]").forEach((button) => {
    button.addEventListener("click", () => toggleBubble(button.dataset.bubbleExpand ?? ""));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-bubble-delete]").forEach((button) => {
    button.addEventListener("click", () => void deleteBubble(button.dataset.bubbleDelete ?? ""));
  });
  document.querySelector("#fileInput")?.addEventListener("change", (event) => void uploadSelectedFile(event));
  document.querySelectorAll<HTMLButtonElement>(".download").forEach((button) => {
    button.addEventListener("click", () => void downloadFile(button.dataset.fileId ?? ""));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-file-delete]").forEach((button) => {
    button.addEventListener("click", () => void deleteFile(button.dataset.fileDelete ?? ""));
  });

  // Drag-and-drop on drop zone
  const dropZone = document.querySelector<HTMLLabelElement>("#dropZone");
  if (dropZone && pasteId && !busy) {
    dropZone.addEventListener("dragover", (event) => {
      event.preventDefault();
      dropZone.classList.add("drag-over");
    });
    dropZone.addEventListener("dragleave", () => {
      dropZone.classList.remove("drag-over");
    });
    dropZone.addEventListener("drop", (event) => {
      event.preventDefault();
      dropZone.classList.remove("drag-over");
      const droppedFile = (event as DragEvent).dataTransfer?.files[0];
      if (droppedFile) void uploadFile(droppedFile);
    });
  }
}

// --- API actions ---

async function createPasteDraft(): Promise<void> {
  pasteCode = generateCode();
  keyText = null;
  pasteKey = await deriveKeyFromCode(pasteCode);
  pasteId = pasteCode;
  const created = await api<{ id: string; expiresAt: string | null }>("/api/pastes", {
    method: "POST",
    body: JSON.stringify({ id: pasteCode, retention })
  });
  expiresAt = created.expiresAt;
  files = [];
  history.pushState(null, "", `/c/${pasteCode}`);
}

async function createPaste(): Promise<void> {
  busy = true;
  setStatus("Creating paste…", "info");
  render();
  try {
    await createPasteDraft();
    bubbles = [];
    draftText = "";
    expandedBubbles.clear();
    setStatus("Paste created.", "success");
  } catch (error) {
    handleApiError(error);
  } finally {
    busy = false;
    render({ preserveTextarea: false });
  }
}

async function loadPaste(): Promise<void> {
  if (!pasteId || !pasteKey) return;
  draftText = document.querySelector<HTMLTextAreaElement>("#pasteText")?.value ?? draftText;
  busy = true;
  setStatus("Syncing…", "info");
  render();
  try {
    const paste = await api<PasteResponse>(`/api/pastes/${pasteId}`);
    bubbles = paste.text ? parseTextBubbles(await decryptText(pasteKey, paste.text)) : [];
    expandedBubbles.clear();
    expiresAt = paste.expiresAt;
    files = await Promise.all(
      paste.files.map(async (file) => ({
        ...file,
        name: await decryptText(pasteKey as CryptoKey, file.encryptedName),
        type: await decryptText(pasteKey as CryptoKey, file.encryptedType)
      }))
    );
    setStatus("Synced.", "success");
  } catch (error) {
    handleApiError(error);
  } finally {
    busy = false;
    render({ preserveTextarea: false });
  }
}

async function saveText(): Promise<void> {
  const textarea = document.querySelector<HTMLTextAreaElement>("#pasteText");
  draftText = textarea?.value ?? "";
  if (!draftText.trim()) {
    setStatus("Enter text before adding a bubble.", "error");
    render();
    return;
  }
  busy = true;
  setStatus(pasteId ? "Encrypting & saving…" : "Creating paste…", "info");
  render();
  try {
    if (!pasteId) {
      await createPasteDraft();
    }
    if (!pasteId || !pasteKey) {
      throw new Error("Paste is not ready.");
    }
    const nextBubbles = bubbles.concat({ id: crypto.randomUUID(), text: draftText, createdAt: new Date().toISOString() });
    const payload = await encryptText(pasteKey, serializeTextBubbles(nextBubbles));
    await api(`/api/pastes/${pasteId}/text`, { method: "PUT", body: JSON.stringify(payload) });
    bubbles = nextBubbles;
    draftText = "";
    setStatus("Text bubble added.", "success");
  } catch (error) {
    handleApiError(error);
  } finally {
    busy = false;
    render({ preserveTextarea: false });
  }
}

async function uploadFile(file: File): Promise<void> {
  if (!pasteId || !pasteKey) return;
  if (file.size >= MAX_FILE_BYTES) {
    setStatus(`File must be ${MAX_FILE_LABEL}.`, "error");
    render();
    return;
  }

  busy = true;
  setStatus("Encrypting file…", "info");
  render();
  try {
    const init = await api<{ uploadId: string; fileId: string }>(`/api/pastes/${pasteId}/files/init`, {
      method: "POST",
      body: JSON.stringify({
        encryptedName: await encryptText(pasteKey, file.name),
        encryptedType: await encryptText(pasteKey, file.type || "application/octet-stream"),
        plainSize: file.size
      })
    });

    let partNumber = 0;
    for (let offset = 0; offset < file.size; offset += FILE_CHUNK_BYTES) {
      const plain = new Uint8Array(await file.slice(offset, offset + FILE_CHUNK_BYTES).arrayBuffer());
      const encrypted = await encryptBytes(pasteKey, plain);
      await api(
        `/api/pastes/${pasteId}/files/${init.uploadId}/parts/${partNumber}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/octet-stream", "X-IV": encrypted.iv },
          body: toArrayBuffer(encrypted.bytes)
        },
        false
      );
      partNumber += 1;
      const progress = Math.min(Math.round((offset + FILE_CHUNK_BYTES) / file.size * 100), 100);
      setStatus(`Uploading… ${progress}%`, "info");
      render();
    }

    await api(`/api/pastes/${pasteId}/files/${init.uploadId}/complete`, { method: "POST" });
    setStatus("File uploaded.", "success");
    await loadPaste();
  } catch (error) {
    handleApiError(error);
  } finally {
    busy = false;
    render();
  }
}

async function uploadSelectedFile(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  await uploadFile(file);
}

async function downloadFile(fileId: string): Promise<void> {
  if (!pasteId || !pasteKey || !fileId) return;
  busy = true;
  setStatus("Downloading…", "info");
  render();
  try {
    const manifest = await api<{ file: StoredFile }>(`/api/pastes/${pasteId}/files/${fileId}`);
    const name = await decryptText(pasteKey, manifest.file.encryptedName);
    const type = await decryptText(pasteKey, manifest.file.encryptedType);
    const chunks: Uint8Array[] = [];

    for (const part of manifest.file.parts) {
      const response = await apiFetch(`/api/pastes/${pasteId}/files/${fileId}/parts/${part.partNumber}`);
      const bytes = new Uint8Array(await response.arrayBuffer());
      chunks.push(await decryptBytes(pasteKey, part.iv, bytes));
    }

    const blob = new Blob(chunks.map(toArrayBuffer), { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name || "download";
    link.click();
    URL.revokeObjectURL(url);
    setStatus("File decrypted.", "success");
  } catch (error) {
    handleApiError(error);
  } finally {
    busy = false;
    render();
  }
}

async function deleteFile(fileId: string): Promise<void> {
  if (!pasteId || !fileId) return;
  const file = files.find((item) => item.id === fileId);
  if (!file || !window.confirm(`Delete “${file.name || "download"}”? This cannot be undone.`)) return;

  busy = true;
  setStatus("Deleting file…", "info");
  render();
  try {
    await api(`/api/pastes/${pasteId}/files/${fileId}`, { method: "DELETE" });
    files = files.filter((item) => item.id !== fileId);
    setStatus("File deleted.", "success");
  } catch (error) {
    handleApiError(error);
  } finally {
    busy = false;
    render();
  }
}

async function deletePaste(): Promise<void> {
  if (!pasteId) return;
  busy = true;
  setStatus("Deleting…", "info");
  render();
  try {
    await api(`/api/pastes/${pasteId}`, { method: "DELETE" });
    pasteId = null;
    pasteCode = null;
    pasteKey = null;
    keyText = null;
    bubbles = [];
    draftText = "";
    expandedBubbles.clear();
    expiresAt = null;
    files = [];
    history.pushState(null, "", "/");
    setStatus("Paste deleted.", "success");
  } catch (error) {
    handleApiError(error);
  } finally {
    busy = false;
    render({ preserveTextarea: false });
  }
}

async function copyLink(): Promise<void> {
  const shareLink = getShareLink();
  if (!shareLink) return;
  await navigator.clipboard.writeText(shareLink);
  setStatus("Link copied.", "success");
  render();
}

async function copyText(): Promise<void> {
  if (!bubbles.length) return;
  await navigator.clipboard.writeText(bubbles.map((bubble) => bubble.text).join("\n\n"));
  setStatus("All text copied.", "success");
  render();
}

async function copyBubble(id: string): Promise<void> {
  const bubble = bubbles.find((item) => item.id === id);
  if (!bubble) return;
  await navigator.clipboard.writeText(bubble.text);
  setStatus("Text copied.", "success");
  render();
}

function toggleBubble(id: string): void {
  if (expandedBubbles.has(id)) {
    expandedBubbles.delete(id);
  } else {
    expandedBubbles.add(id);
  }
  render();
}

async function deleteBubble(id: string): Promise<void> {
  if (!pasteId || !pasteKey || !bubbles.some((bubble) => bubble.id === id)) return;
  if (!window.confirm("Delete this text bubble? This cannot be undone.")) return;

  const nextBubbles = withoutTextBubble(bubbles, id);
  busy = true;
  setStatus("Deleting text bubble…", "info");
  render();
  try {
    const payload = await encryptText(pasteKey, serializeTextBubbles(nextBubbles));
    await api(`/api/pastes/${pasteId}/text`, { method: "PUT", body: JSON.stringify(payload) });
    bubbles = nextBubbles;
    expandedBubbles.delete(id);
    setStatus("Text bubble deleted.", "success");
  } catch (error) {
    handleApiError(error);
  } finally {
    busy = false;
    render();
  }
}

function openPaste(): void {
  const input = document.querySelector<HTMLInputElement>("#openCode");
  if (!input?.value) return;
  const target = parseOpenTarget(input.value);
  if (target) {
    location.href = target;
    return;
  }
  const code = input.value.trim().toLowerCase();
  if (!/^[0-9a-z]{4}-[0-9a-z]{4}$/.test(code)) {
    setStatus("Paste a full share link, or use xxxx-xxxx for code-only lookup.", "error");
    render();
    return;
  }
  location.href = `/c/${code}`;
}

// --- HTTP helpers ---

async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && typeof init.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(path, { ...init, headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: `Request failed with ${response.status}` }));
    throw new Error(String(body.error ?? `Request failed with ${response.status}`));
  }
  return response;
}

async function api<T = { ok: boolean }>(path: string, init: RequestInit = {}, parseJson = true): Promise<T> {
  const response = await apiFetch(path, init);
  return parseJson ? response.json() : ({ ok: true } as T);
}

function handleApiError(error: unknown): void {
  setStatus(messageFor(error), "error");
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}

// --- Utilities ---

function getPasteId(): string | null {
  const code = getShortCode();
  if (code) return code;
  const match = location.pathname.match(/^\/p\/([A-Za-z0-9_-]+)$/);
  return match?.[1] ?? null;
}

function getShareLink(): string {
  if (pasteCode) return `${location.origin}/c/${pasteCode}`;
  if (pasteId && keyText) return `${location.origin}/p/${pasteId}#k=${keyText}`;
  return "";
}

function parseOpenTarget(value: string): string | null {
  const trimmed = value.trim();
  const match = trimmed.match(/(?:^|\/)c\/([0-9a-z]{4}-[0-9a-z]{4})(#k=[A-Za-z0-9_-]+)$/i);
  if (match) return `/c/${match[1].toLowerCase()}${match[2]}`;
  const codeWithKey = trimmed.match(/^([0-9a-z]{4}-[0-9a-z]{4})(#k=[A-Za-z0-9_-]+)$/i);
  if (codeWithKey) return `/c/${codeWithKey[1].toLowerCase()}${codeWithKey[2]}`;
  const codeOnly = trimmed.match(/(?:^|\/)c\/([0-9a-z]{4}-[0-9a-z]{4})$/i);
  if (codeOnly) return `/c/${codeOnly[1].toLowerCase()}`;
  return null;
}

function generateCode(): string {
  const chars = "0123456789abcdefghijklmnopqrstuvwxyz";
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  let code = "";
  for (let i = 0; i < 8; i += 1) {
    code += chars[bytes[i] % chars.length];
    if (i === 3) code += "-";
  }
  return code;
}

function getShortCode(): string | null {
  const match = location.pathname.match(/^\/c\/([0-9a-z]{4}-[0-9a-z]{4})$/);
  return match?.[1] ?? null;
}

function getKeyText(): string | null {
  const params = new URLSearchParams(location.hash.startsWith("#") ? location.hash.slice(1) : location.hash);
  const key = params.get("k");
  if (key) return key;
  return location.hash.startsWith("#k=") ? location.hash.slice(3) : null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.parse(iso) - Date.now();
  if (diff <= 0) return "Expired";
  const hours = Math.floor(diff / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 0) return `Expires in ${hours}h ${mins}m`;
  return `Expires in ${mins}m`;
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}
